import React, { useEffect, useMemo, useReducer } from "react";
import {
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import GetImages from "@/components/GetImages";
import TagChip from "@/components/TagChip";
import DoctorReviews from "@/components/DoctorReviews";
import Loader from "@/components/Loader";
import ViewClinics from "./ViewClinics";
import Breadcrumb from "@/components/Breadcrumb";
import MakeAppointment from "@/components/DoctorProfile/MakeAppointment";
import CollapsibleText from "@/components/CollapsibleText";
import ContactForm from "@/components/ContactForm";
import {
  AppointmentTabPanel,
  AppointmentTabsPanel,
} from "@/components/AppointmentTab";
import Dialog from "@/components/Dialog";
import { getYoutubeEmbedUrl } from "@/views/Patient/VideoDetails/ShortVideoSection";
import SEO from "@/components/SEO";

import style from "./index.module.css";

import noCoverImage from "@/assets/noCoverImage.jpg";

import { IRoleType } from "@/models/role";
import { IMakeAppointmentEvent, IProfileDropdownEnums } from "@/models/doctor";
import { IDoctorPermissionEnums, IModalKeyEnums } from "@/models/permission";
import { IRedirectAfterLoginType, ISessionKeys } from "@/models";

import {
  doctorDegreeName,
  doctorSpeakLanguages,
  randomCardColorGenerator,
  topBarDropdownDispatchingEnums,
} from "@/utils";
import { storage } from "@/utils/Storage";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useMakeAppointment from "@/utils/hooks/useMakeAppointment";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  doctorProfileDetails,
  fetchDoctorProfile,
  fetchDoctorReviewRatingList,
  fetchDoctorVideoReviews,
} from "@/store/slices/doctor/profile";
import { setTopBarDropdownItems } from "@/store/slices/doctor/topNavigation";
import { config, setRedirectAfterLogin } from "@/store/slices/config";
import { appointmentDetails } from "@/store/slices/doctor/appointment";

const breadcrumbsItems = [
  { label: "Home", link: "/home" },
  { label: "Doctors", link: "/find-doctor" },
];

interface IProps {
  doctorID?: string;
}

const initialState = {
  viewMoreClinics: false,
  openAppointmentSheet: false,
  isReviewVideoOpen: false,
  reviewVideoLink: "",
};

type IState = typeof initialState;

enum IDoctorProfileActionType {
  SET_VIEW_MORE_CLINICS = "SET_VIEW_MORE_CLINICS",
  SET_OPEN_APPOINTMENT_SHEET = "SET_OPEN_APPOINTMENT_SHEET",
  SET_IS_REVIEW_VIDEO_OPEN = "SET_IS_REVIEW_VIDEO_OPEN",
  SET_REVIEW_VIDEO_LINK = "SET_REVIEW_VIDEO_LINK",
}

type IDoctorProfileAction =
  | { type: IDoctorProfileActionType.SET_VIEW_MORE_CLINICS; payload: boolean }
  | {
      type: IDoctorProfileActionType.SET_OPEN_APPOINTMENT_SHEET;
      payload: boolean;
    }
  | {
      type: IDoctorProfileActionType.SET_IS_REVIEW_VIDEO_OPEN;
      payload: boolean;
    }
  | {
      type: IDoctorProfileActionType.SET_REVIEW_VIDEO_LINK;
      payload: string;
    };

const reducer = (state: IState, action: IDoctorProfileAction): IState => {
  switch (action.type) {
    case IDoctorProfileActionType.SET_VIEW_MORE_CLINICS:
      return { ...state, viewMoreClinics: action.payload };
    case IDoctorProfileActionType.SET_OPEN_APPOINTMENT_SHEET:
      return { ...state, openAppointmentSheet: action.payload };
    case IDoctorProfileActionType.SET_IS_REVIEW_VIDEO_OPEN:
      return { ...state, isReviewVideoOpen: action.payload };
    case IDoctorProfileActionType.SET_REVIEW_VIDEO_LINK:
      return { ...state, reviewVideoLink: action.payload };
    default:
      return state;
  }
};
const DoctorProfile: React.FC<IProps> = (props) => {
  const { doctorID } = props;

  const [state, dispatch] = useReducer(reducer, initialState);
  const { viewMoreClinics, openAppointmentSheet, isReviewVideoOpen } = state;

  const keys = [ISessionKeys.ROLE_TYPE];
  const [roleType] = keys.map((keyItem) => storage.getSessionToken(keyItem));

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const isDoctorOrJrDoctor =
    roleType === IRoleType.DOCTOR || roleType === IRoleType.JR_DOCTOR;

  const isSmallScreen = useMemo(
    () => isMobile || isTablet,
    [isMobile, isTablet]
  );

  const { openScheduleModalHandler, selectedTab, setSelectedTab } =
    useMakeAppointment();

  const permissionRestrictionHandler = usePermissionRestriction();

  const appDispatch = useAppDispatch();
  const { redirectAfterLogin } = useAppSelector(config);
  const { slot } = useAppSelector(appointmentDetails);
  const {
    detail: doctorProfile,
    videoReviews,
    isDetailLoading,
    isRatingReviewLoading,
    isVideoReviewsLoading,
  } = useAppSelector(doctorProfileDetails);

  const navigate = useNavigate();
  const { state: locationState } = useLocation();

  const doctorId =
    locationState?.doctorId || doctorID || redirectAfterLogin?.doctorId;

  const seo = {
    title: `${doctorProfile?.name} – Verified ${doctorProfile?.speciality} in ${doctorProfile?.city} | Global Neuro and Spine Care`,
    description: `${doctorProfile?.name} is a verified ${doctorProfile?.speciality} in ${doctorProfile?.city} on Global Neuro and Spine Care. Book online consults, view reviews, clinic details & fees. Trusted neuro care across India.`,
    keywords: `${doctorProfile?.name}, ${doctorProfile?.speciality} in ${doctorProfile?.city}, best neurosurgeon in ${doctorProfile?.city}, top neurologist ${doctorProfile?.city}, brain doctor ${doctorProfile?.city}, neurologist near me, verified neuro doctor, Global Neuro and Spine Care`,
  };

  const isSlotNotAvailable = Object.values(slot).every(
    (item) => item?.length === 0
  );

  const backToFindDoctorPage = () => {
    if (isDoctorOrJrDoctor) {
      navigate("/profile", { state: { navItem: 3 } });
      return;
    }
    navigate("/find-doctor", { state: { navItem: 1 } });
  };

  const handleViewMore = () => {
    dispatch({
      type: IDoctorProfileActionType.SET_VIEW_MORE_CLINICS,
      payload: !state.viewMoreClinics,
    });
  };

  const handleAppointmentTab = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setSelectedTab(newValue);
    dispatch({
      type: IDoctorProfileActionType.SET_OPEN_APPOINTMENT_SHEET,
      payload: !state.openAppointmentSheet,
    });

    if (roleType === IRoleType.GUEST) {
      appDispatch(
        setRedirectAfterLogin({
          ...redirectAfterLogin,
          selectedTab: newValue,
        })
      );
    }
  };

  useEffect(() => {
    if (
      roleType === IRoleType.PATIENT ||
      roleType === IRoleType.GUEST ||
      doctorProfile
    ) {
      const isPermissionGranted = permissionRestrictionHandler(
        IModalKeyEnums.DOCTOR,
        IDoctorPermissionEnums.GET_DOCTOR_PROFILE_BY_DOCTOR
      );
      if (!isPermissionGranted) {
        appDispatch(
          setTopBarDropdownItems(
            topBarDropdownDispatchingEnums[IProfileDropdownEnums.PROFILE]
          )
        );
        return;
      }

      appDispatch(fetchDoctorProfile(doctorId));
      appDispatch(fetchDoctorReviewRatingList({ doctorId: doctorId! }));
      appDispatch(fetchDoctorVideoReviews(doctorId!));
    }
  }, []);

  useEffect(() => {
    if (redirectAfterLogin?.type === IRedirectAfterLoginType.DOCTOR_PROFILE) {
      dispatch({
        type: IDoctorProfileActionType.SET_OPEN_APPOINTMENT_SHEET,
        payload: true,
      });
      setSelectedTab(Number(redirectAfterLogin?.selectedTab));
    }
  }, [redirectAfterLogin]);

  useEffect(() => {
    if (!doctorProfile) {
      appDispatch(fetchDoctorProfile(undefined));
    }
  }, []);

  if (
    isDetailLoading ||
    ((roleType === IRoleType.PATIENT || roleType === IRoleType.GUEST) &&
      (isRatingReviewLoading || isVideoReviewsLoading))
  ) {
    return <Loader />;
  }

  const extractVideoID = (url: string) => {
    const regex =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([^\s&?/]+)/;
    const match = url.match(regex);
    const thumbnailUrl = `https://img.youtube.com/vi/${
      match ? match[1] : null
    }/hqdefault.jpg`;
    return thumbnailUrl;
  };

  const videoReviewOpenCloseHandler = () =>
    dispatch({
      type: IDoctorProfileActionType.SET_IS_REVIEW_VIDEO_OPEN,
      payload: !state.isReviewVideoOpen,
    });

  const reviewHandler = (videoLink: string) => {
    dispatch({
      type: IDoctorProfileActionType.SET_REVIEW_VIDEO_LINK,
      payload: videoLink,
    });
    videoReviewOpenCloseHandler();
  };

  if (isDoctorOrJrDoctor) {
    return (
      <Container maxWidth={"xl"} disableGutters>
        <Grid container justifyContent={"space-between"}>
          <Grid item xs={12} overflow={"hidden"} borderRadius={{ lg: 2.5 }}>
            <Stack direction={"column"}>
              <Box position={"relative"}>
                <img
                  src={
                    doctorProfile?.cover_photo ??
                    doctorProfile?.clinics[0]?.photo_url
                  }
                  alt="clinicImage"
                  className={style.clinicImage}
                  onError={(e) => (e.currentTarget.src = noCoverImage)}
                />
                <Box
                  position={"absolute"}
                  top={isDoctorOrJrDoctor ? "66%" : "80%"}
                  left={"50%"}
                  sx={{ transform: "translateX(-50%)" }}
                >
                  <img
                    className={style.doctorImage}
                    src={doctorProfile?.photo_url}
                    alt="doctorPhoto"
                  />
                  <Box className={style.doctorProfileStatus}>
                    <Chip
                      label="Verified"
                      variant="filled"
                      sx={{
                        bgcolor: "var(--peach-200)",
                        fontWeight: 500,
                        py: 0,
                        height: 24,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              {isSmallScreen && (
                <Grid
                  item
                  className={style.backNavigation}
                  onClick={backToFindDoctorPage}
                >
                  <GetImages name="LeftArrowIcon" width="16" height="16" />
                </Grid>
              )}
              <Grid
                item
                xs={12}
                className={style.container}
                sx={{
                  flex: 1,
                  paddingTop: isDoctorOrJrDoctor ? "8rem" : "5rem",
                }}
              >
                <Grid item className={style.doctorDetailContainer}>
                  <Grid className={style.doctorInfoContainer}>
                    <Typography component={"h4"}>
                      {doctorProfile?.name}
                    </Typography>
                    <Typography component={"h6"}>
                      {doctorProfile?.experience_years &&
                        doctorProfile?.experience_years + " yrs • "}
                      {doctorProfile?.speciality &&
                        doctorProfile?.speciality + " • "}
                      {doctorProfile?.qualification_details &&
                        doctorDegreeName(doctorProfile?.qualification_details)}
                    </Typography>
                    <Typography component={"h6"}>
                      {doctorProfile?.language &&
                        doctorSpeakLanguages(doctorProfile?.language)}
                    </Typography>
                    {doctorProfile?.clinics?.slice(0, 3).map((item) => (
                      <Typography component={"p"} key={item.address}>
                        {item?.address}, {item?.city}-{item?.pincode},{" "}
                        {item?.distance!.toFixed(2)}km
                      </Typography>
                    ))}
                  </Grid>
                  {doctorProfile && doctorProfile?.clinics.length > 3 && (
                    <Grid item>
                      <Typography
                        component={"p"}
                        className={style.totalClinicCount}
                        onClick={handleViewMore}
                      >
                        {isSmallScreen ? (
                          `${doctorProfile?.clinics.length} more clinics`
                        ) : (
                          <FormattedMessage
                            id="view_more"
                            defaultMessage={"View More"}
                          />
                        )}
                      </Typography>
                    </Grid>
                  )}
                  {doctorProfile?.about_me && (
                    <Grid
                      xs={12}
                      item
                      border={`1px solid`}
                      borderColor={"var(--gray-200)"}
                      borderLeft={"none"}
                      borderRight={"none"}
                      p={1}
                    >
                      <CollapsibleText
                        text={doctorProfile?.about_me ?? ""}
                        color="var(--gray-500)"
                        fontWeight={400}
                      />
                    </Grid>
                  )}
                  <Grid item className={style.tagsContainer}>
                    <Typography component={"p"}>
                      <FormattedMessage
                        id="profile_help_text"
                        defaultMessage={"I can help you with"}
                      />
                    </Typography>
                    <Grid item className={style.tagItemsContainer}>
                      {doctorProfile?.tags
                        ?.split(",")
                        ?.map((tagName, index) => (
                          <TagChip
                            tagName={tagName}
                            key={`${tagName}-${index}`}
                          />
                        ))}
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid item className={style.reviewCountHeader}>
                      <GetImages name="LikeIcon" width="20" height="16" />
                      <Typography component={"p"}>
                        {doctorProfile?.reviews} Reviews
                      </Typography>
                    </Grid>
                    <Grid item className={style.expertiseContainer}>
                      <Typography component={"p"}>
                        {doctorProfile?.expertise}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                {viewMoreClinics && (
                  <ViewClinics
                    isOpen={viewMoreClinics}
                    closeModalHandler={handleViewMore}
                    clinics={doctorProfile?.clinics!}
                  />
                )}
              </Grid>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <>
      <SEO {...seo} />
      <Container
        maxWidth={"xl"}
        disableGutters={isSmallScreen}
        sx={{ my: { lg: 4 }, mb: { xs: 20, lg: 0 } }}
      >
        <Grid container gap={4} justifyContent={"center"} mb={5}>
          <Grid item xs={11} sx={{ display: { xs: "none", md: "block" } }}>
            <Breadcrumb
              items={[
                ...breadcrumbsItems,
                {
                  label: doctorProfile?.speciality ?? "",
                  link: `/find-doctor?type=${doctorProfile?.speciality}`,
                },
                {
                  label: doctorProfile?.name ?? "",
                  isCurrent: true,
                },
              ]}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={7}
            overflow={"hidden"}
            borderRadius={{ lg: 2.5 }}
          >
            <Stack direction={"column"} position={"relative"}>
              <Box>
                <img
                  src={
                    doctorProfile?.cover_photo ??
                    doctorProfile?.clinics[0]?.photo_url
                  }
                  alt="clinicImage"
                  className={style.clinicImage}
                />
              </Box>
            </Stack>
            {isSmallScreen && (
              <IconButton
                className={style.backNavigation}
                onClick={backToFindDoctorPage}
              >
                <GetImages name="LeftArrowIcon" width="16" height="16" />
              </IconButton>
            )}
            <Grid
              container
              width={"100%"}
              className={style.container}
              sx={{
                flex: 1,
                paddingTop: isDoctorOrJrDoctor ? "8rem" : "5rem",
              }}
            >
              <Grid
                item
                xs={12}
                position={"relative"}
                mt={{ xs: "-30%", sm: "-15%", md: "-20%", lg: "-15%" }}
              >
                <img
                  className={style.doctorImage}
                  src={doctorProfile?.photo_url}
                  alt="doctorPhoto"
                />
                <Box className={style.doctorProfileStatus}>
                  <Chip
                    label="Verified"
                    variant="filled"
                    sx={{
                      bgcolor: "var(--peach-200)",
                      fontWeight: 500,
                      py: 0,
                      height: 24,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} className={style.doctorDetailContainer}>
                <Stack
                  direction={"column"}
                  gap={0.5}
                  mb={1}
                  className={style.doctorInfoContainer}
                >
                  <Typography component={"h4"}>
                    {doctorProfile?.name}
                  </Typography>
                  <Typography component={"h6"}>
                    {doctorProfile?.experience_years &&
                      doctorProfile?.experience_years + " yrs • "}
                    {doctorProfile?.speciality} •{" "}
                    {doctorProfile?.qualification_details &&
                      doctorDegreeName(doctorProfile?.qualification_details)}
                  </Typography>
                  <Typography component={"h6"}>
                    {doctorProfile?.language &&
                      doctorSpeakLanguages(doctorProfile?.language)}
                  </Typography>
                  {doctorProfile?.clinics?.slice(0, 3).map((item) => (
                    <Typography component={"p"} key={item.address}>
                      {item?.name}, {item?.address}, {item?.city}-
                      {item?.pincode}, {item?.distance!.toFixed(2)}km
                    </Typography>
                  ))}
                </Stack>
                {doctorProfile && doctorProfile?.clinics.length > 3 && (
                  <Grid item>
                    <Typography
                      component={"p"}
                      className={style.totalClinicCount}
                      onClick={handleViewMore}
                    >
                      {isSmallScreen ? (
                        `${doctorProfile?.clinics.length} more clinics`
                      ) : (
                        <FormattedMessage
                          id="view_more"
                          defaultMessage={"View More"}
                        />
                      )}
                    </Typography>
                  </Grid>
                )}
                {doctorProfile?.profile_tags && (
                  <Grid item className={style.profileTagsContainer}>
                    {doctorProfile?.profile_tags?.map((tag) => (
                      <Typography
                        component={"p"}
                        className={style.profileTags}
                        textAlign={"left"}
                        sx={{ backgroundColor: randomCardColorGenerator() }}
                      >
                        {tag}
                      </Typography>
                    ))}
                  </Grid>
                )}
                <Grid
                  xs={12}
                  item
                  border={`1px solid`}
                  borderColor={"var(--gray-200)"}
                  borderLeft={"none"}
                  borderRight={"none"}
                  p={1}
                >
                  <CollapsibleText
                    text={doctorProfile?.about_me ?? ""}
                    color="var(--gray-500)"
                    fontWeight={400}
                  />
                </Grid>
                <Grid item className={style.tagsContainer}>
                  <Typography component={"p"}>
                    <FormattedMessage
                      id="profile_help_text"
                      defaultMessage={"I can help you with"}
                    />
                  </Typography>
                  <Grid item className={style.tagItemsContainer}>
                    {doctorProfile?.tags.split(",").map((tagName, index) => (
                      <TagChip tagName={tagName} key={`${tagName}-${index}`} />
                    ))}
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid item className={style.reviewCountHeader}>
                    <GetImages name="LikeIcon" width="20" height="16" />
                    <Typography component={"p"}>
                      {doctorProfile?.reviews} Reviews
                    </Typography>
                  </Grid>
                  <Grid item className={style.expertiseContainer}>
                    <Typography component={"p"}>
                      {doctorProfile?.expertise}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {(roleType === IRoleType.PATIENT ||
                roleType === IRoleType.GUEST) &&
                videoReviews?.length > 0 && (
                  <>
                    <Grid item xs={12} className={style.videoReviewContainer}>
                      <Box className={style.videoReviewHeader}>
                        <Typography component={"h6"}>
                          <FormattedMessage
                            id="video_reviews"
                            defaultMessage={"Video Reviews"}
                          />
                        </Typography>
                      </Box>
                      <Stack
                        direction={"row"}
                        className={style.videoReviewThumbnail}
                      >
                        {videoReviews.map((review, index) => (
                          <img
                            key={review.id}
                            src={extractVideoID(review.video_link)}
                            alt={review.heading}
                            className={style.videoReviewsThumbnail}
                            loading="lazy"
                            onClick={() => reviewHandler(review.video_link)}
                          />
                        ))}
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <DoctorReviews />
                    </Grid>
                  </>
                )}
            </Grid>
          </Grid>
          <Grid item md={4.5} lg={3.8} display={{ xs: "none", md: "block" }}>
            <Box
              width={"100%"}
              sx={{
                bgcolor: "var(--neutral-100)",
                borderRadius: 5,
                padding: 0,
              }}
            >
              <MakeAppointment
                doctorId={doctorId}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                openScheduleModalHandler={openScheduleModalHandler}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Stack
        justifyContent={"center"}
        alignItems={"center"}
        bgcolor={"var(--neutral-100)"}
        display={{ xs: "none", lg: "flex" }}
        py={3}
      >
        <ContactForm />
      </Stack>
      {!isSlotNotAvailable && (
        <Stack
          position={"fixed"}
          bottom={0}
          display={{ xs: "flex", md: "none" }}
          width={"calc(100% - 30px)"}
          bgcolor={"var(--gray-50)"}
          p={2}
          gap={1}
          justifyContent={"center"}
        >
          <Typography variant="subtitle2" textAlign={"center"}>
            <FormattedMessage
              id="book_appointment"
              defaultMessage={"Book an appointment"}
            />
          </Typography>
          <Box width={"100%"}>
            <AppointmentTabsPanel
              onChange={handleAppointmentTab}
              aria-label="basic tabs example"
              className={style.tabBtnContainer}
            >
              <AppointmentTabPanel
                value={IMakeAppointmentEvent.VIDEO_CALL}
                label={
                  <Grid item className={style.tabInnerContainer}>
                    <GetImages
                      name={false ? "VideoIcon" : "VideoWhiteIcon"}
                      width="20"
                      height="20"
                    />
                    <Typography component={"p"}>
                      <FormattedMessage
                        id="video_call"
                        defaultMessage="Video Call"
                      />
                    </Typography>
                  </Grid>
                }
              />
              <Divider orientation="vertical" flexItem />
              <AppointmentTabPanel
                value={IMakeAppointmentEvent.IN_CLINIC}
                label={
                  <Grid item className={style.tabInnerContainer} width={"100%"}>
                    <GetImages
                      name={false ? "ClinicWhiteIcon" : "ClinicIcon"}
                      width="20"
                      height="20"
                    />
                    <Typography component={"p"}>
                      <FormattedMessage
                        id="in_clinic"
                        defaultMessage="In-Clinic"
                      />
                    </Typography>
                  </Grid>
                }
              />
            </AppointmentTabsPanel>
          </Box>
        </Stack>
      )}
      <ViewClinics
        isOpen={viewMoreClinics}
        closeModalHandler={handleViewMore}
        clinics={doctorProfile?.clinics!}
      />
      {isSmallScreen && (
        <MakeAppointment
          open={openAppointmentSheet}
          doctorId={doctorId}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          openScheduleModalHandler={openScheduleModalHandler}
          onClose={() =>
            dispatch({
              type: IDoctorProfileActionType.SET_OPEN_APPOINTMENT_SHEET,
              payload: false,
            })
          }
        />
      )}
      {isReviewVideoOpen && (
        <Dialog
          isOpen={isReviewVideoOpen}
          closeHandler={videoReviewOpenCloseHandler}
          fullWidth
          maxWidth={"md"}
          paperProps={{
            sx: {
              height: "100%",
            },
          }}
        >
          <Box height={"100%"} borderRadius={4}>
            <iframe
              src={getYoutubeEmbedUrl(videoReviews[0].video_link)}
              title={videoReviews[0].heading}
              className={style.iframeContainer}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="autoplay"
              allowFullScreen
            />
          </Box>
        </Dialog>
      )}
    </>
  );
};

export default DoctorProfile;
